import { Component, OnInit, OnChanges } from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { Message } from "primeng/primeng";
import { Subscription } from "rxjs/Rx";
import { AccountService, AccountViewService, DeletePendingEmailService } from "./account.service";
import {
  ContactInfo,
  PhoneInfo,
  UserAccountSettingView,
  PhoneVerificationRequest
} from "./model";
import { TimeService } from "../time.service";
import { PhoneVerificationService } from "./phone-verify.service";
import { NgxTranslateService } from "../shared/ngx-translate.service";

@Component({
  moduleId: module.id.toString(),
  template: require('./account.component.html')
})
export class AccountComponent implements OnInit, OnChanges {
  setting: UserAccountSettingView;
  pendingEmail: string = null;
  busy: Subscription;
  messages: Message[] = [];
  duplicateEmailError: string = "";
  phoneTypes: string[];
  accountForm: FormGroup;
  email: AbstractControl;
  firstName: AbstractControl;
  lastName: AbstractControl;
  PHONE_VALIDATION: string = "[0-9]{3}-[0-9]{3}-[0-9]{4}";
  EMAIL_VALIDATION: string = "^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$";
  DEFAULT_STATUS: string = "NotVerified";
  DEFAULT_PHONETYPE: string = "Mobile";
  languages: { id: string, label: string }[];
  constructor(
    private accountService: AccountService,
    private accountViewService: AccountViewService,
    private deletePendingEmailService: DeletePendingEmailService,
    private timeservice: TimeService,
    private formBuilder: FormBuilder,
    private verifyService: PhoneVerificationService,
    private translateService: NgxTranslateService
  ) {
    this.phoneTypes = [this.DEFAULT_PHONETYPE, "Work", "Home"];
    this.languages = [{ id: 'en', label: 'English' },
    { id: 'fr', label: 'Français' },
    { id: 'es', label: 'Español' }
    ];

    this.createForm();
    this.pendingEmail = null; 
  }

  ///////////////////////////////////////////////////////////////////////////
  // Implementations
  ///////////////////////////////////////////////////////////////////////////

  // Handles the page load event.
  ngOnInit(): void {
    this.busy = this.accountViewService.getSettingsView().subscribe(
      sv => {
        this.setting = sv;
        this.pendingEmail = sv.contact && sv.contact.pendingEmail ? sv.contact.pendingEmail : null;
        this.ngOnChanges();
      },
      error => {
        console.log(error);
        this.messages.push({
          severity: "error",
          detail: this.translateService.getText("Acc_Error_Verification", "There was a problem retrieving your account settings."),
          summary: this.translateService.getText("ErrorSummary", "Error!")
        });
      }
    );
  }

  // Called anytime the underlying view model has changes that need to update
  // their associated form controls.
  ngOnChanges(): void {


    // Add validators for fields that are editable only when the user is not a Paycor employee.
    // If this is not done, then the form can be in an "INVALID" state for fields that Paycor
    // employees cannot edit, thereby making it impossible to save the form.
    if (this.setting.isPaycorUser) {
      this.accountForm.controls["firstName"].clearValidators();
      this.accountForm.controls["lastName"].clearValidators();
      this.accountForm.controls["email"].clearValidators();
    }
    // Reset the form to match the underlying view model. If the user presses the "Cancel"
    // button, then entire form will go back to this state.
    this.accountForm.reset({
      firstName: this.setting.contact.firstName,
      lastName: this.setting.contact.lastName,
      email: this.setting.contact.email,
      cultureInfo: this.setting.security.cultureInfo
    });
    this.setPhones(this.setting.contact.phones);
    this.setTranslationLanguage();
  }

  ///////////////////////////////////////////////////////////////////////////
  // Public methods
  ///////////////////////////////////////////////////////////////////////////

  // This method is called to test if a form control is valid and pristine.
  // If the test fails then alloy styling is changed to activate the
  // tooltip error associated with this form control.
  public setClassInvalid(control: FormControl, input: string): string {
    if (control === undefined) return input;
    let test = control.pristine || control.valid;
    return test ? input : input + " tooltip-bottom error";
  }

  // Special styling for email input when there's a duplicate email error
  public setEmailClass(control: FormControl, input: string): string {
    if (control === undefined) return input;
    let test = control.pristine || control.valid;
    let hasValidationError = test ? "" : " tooltip-bottom error";
    let hasDuplicateError = this.duplicateEmailError ? " email-input-error" : "";
    return input + hasValidationError + hasDuplicateError;
  }

  // Handles the resetting of the form back to the pristine (undirty) state.
  public onReset(): void {
    this.ngOnChanges();
  }

  // Handles the form submit (save) event.
  public onSubmit(): void {
    const originalEmail = this.setting.contact.email;
    const newEmail = this.accountForm.get('email').value;
    this.setting.contact = this.accountForm.value;
    let shouldReload = this.setting.security.cultureInfo != this.accountForm.controls["cultureInfo"].value;
    this.setting.security.cultureInfo = this.accountForm.controls["cultureInfo"].value;
    this.save(shouldReload, originalEmail, newEmail);
  }

  // Creates a new phone form group and adds it to the phones form array.
  public onAddPhone(): void {
    const control = <FormArray>this.accountForm.controls["phones"];
    control.push(this.createPhone());
    this.accountForm.markAsDirty();
  }

  // Removes an existng phone from the phones form array.
  public onRemovePhone(id: number): void {
    const control = <FormArray>this.accountForm.controls["phones"];
    control.removeAt(id);
    this.accountForm.markAsDirty();
  }

  // Initiates a request to verify the selected phone via SMS. Calling
  // this method has the side effect of saving the entire form, so the form
  // must be in the "VALID" state before calling this method.
  public onVerifyPhone(frmGroup: FormGroup, sendAsSms: boolean): void {
    const phoneInfo: PhoneInfo = {
      id: frmGroup.controls["id"].value,
      phoneType: frmGroup.controls["phoneType"].value,
      isTextEnabled: frmGroup.controls["isTextEnabled"].value,
      number: frmGroup.controls["number"].value,
      verificationStatus: frmGroup.controls["verificationStatus"].value
    };

    const request: PhoneVerificationRequest = {
      phoneInfo: phoneInfo,
      sendAsSms: sendAsSms
    };

    // Set the vie wmodel to match the current form control state.
    this.setting.contact = this.accountForm.value;

    // Verify the associated phone via the requested method (text or voice).
    this.busy = this.verifyService.verifyPhone(request).subscribe(
      req => {
        let idx = this.setting.contact.phones.findIndex(
          p =>
            p.id == phoneInfo.id &&
            p.number == phoneInfo.number &&
            p.phoneType == phoneInfo.phoneType
        );
        this.setting.contact.phones[idx] = req.phoneInfo;
        this.ngOnChanges();
        this.messages.push({
          severity: "success",
          detail: this.translateService.getText("Acc_Not_Ph.NoSMS", "A request to verify your phone number was sent as a text message."),
          summary: this.translateService.getText("Acc_Success_Ph.No", "Verification request sent")
        });
      },
      error => {
        console.log(error);
        this.messages.push({
          severity: "error",
          detail: this.translateService.getText("Acc_Error_Ph.NoSMS", "Unable to initiate the verification text message."),
          summary: this.translateService.getText("Error", "An error occurred.")
        });
      }
    );
  }

  // Determines if the form cannot be reset.
  public get cannotReset(): boolean {
    return this.accountForm.pristine;
  }

  // Determines if the form cannot be submitted.
  public get cannotSubmit(): boolean {
    return this.accountForm.pristine || !this.accountForm.valid || !!this.duplicateEmailError;
  }

  // Determines if the Text Enable/Disable controls should be displayed
  // based on the state of the associated phone form group.
  public allowTextEnablement(frmGroup: FormGroup): boolean {
    return (
      frmGroup.controls["phoneType"].value === this.DEFAULT_PHONETYPE &&
      frmGroup.controls["verificationStatus"].value === "Verified"
    );
  }

  // Sets the css class for the verification state controls based on
  // the associated phone form group.
  public setVerificationClass(frmGroup: FormGroup): string {
    let common = " phone-verify";
    switch (frmGroup.controls["verificationStatus"].value) {
      case "VerificationRequestSent":
        return "ay-icon-refresh" + common;
      case "Verified":
        return "ay-icon-ok" + common;
      case "StopReceived":
        return "ay-icon-ban" + common;
      case "NotVerified":
      default:
        return "ay-icon-warn" + common;
    }
  }

  // Set the tooltip content that explains the verification states based on the
  // associated phone form group.
  public setVerificationHelp(frmGroup: FormGroup): string {
    switch (frmGroup.controls["verificationStatus"].value) {
      case "VerificationRequestSent":
        return this.translateService.getText("Acc_Verification_InProgress", "In Progress: The verification has been sent as a text message and the system is waiting for the reply.");
      case "Verified":
        return this.translateService.getText("Acc_Verification_Verified", "Verified: You may now enable texting and receive notifications on this number.");
      case "StopReceived":
        return this.translateService.getText("Acc_STOPMessage", "Stop Received: The recipient of the notification has replied with a STOP message; therefore, the phone remains unverified.");
      case "NotVerified":
      default:
        return this.translateService.getText("Acc_Verification_NotVerified", "Not Verified: You must verify your number by text before this number can be used to receive notifications.");
    }
  }

  // Determines if the verification controls should be enabled based on
  // the state of the associated phone form group.
  public isVerifyEnabled(frmGroup: FormGroup, sendAsSms: boolean): boolean {
    if (
      frmGroup.controls["phoneType"].value !== this.DEFAULT_PHONETYPE &&
      sendAsSms
    )
      return false;
    return frmGroup.controls["verificationStatus"].value === "NotVerified";
  }

  // Provides the current number of phones the user has for their settings profile based
  // on the phone form array.
  public get phoneCount(): number {
    if (this.accountForm === undefined) return 0;
    const phones: FormArray = <FormArray>this.accountForm.controls["phones"];
    if (phones.controls === undefined) return 0;
    return phones.controls.length;
  }

  // Generated by AI on 03/27/2026
  // Returns the number of phones in the form array that have a Verified status.
  // Reads directly from the live FormArray so the count reflects unsaved additions
  // and deletions without requiring a page reload.
  public get verifiedPhoneCount(): number {
    if (this.accountForm === undefined) return 0;
    const phones = <FormArray>this.accountForm.controls["phones"];
    if (!phones || !phones.controls) return 0;
    return phones.controls.filter(
      c => c.get("verificationStatus").value === "Verified"
    ).length;
  }

  // Generated by AI on 03/27/2026
  // Determines whether the delete button should be shown for a given phone form group.
  // Unverified phones can always be deleted. A verified phone can only be deleted when
  // at least one other verified phone exists, preventing the user from removing their
  // sole MFA-capable number.
  public canDeletePhone(frmGroup: FormGroup): boolean {
    const isVerified = frmGroup.controls["verificationStatus"].value === "Verified";
    if (!isVerified) return true;
    return this.verifiedPhoneCount > 1;
  }

    // Cancels the pending email change and refreshes the form
  public onCancelPendingEmail(): void {
    this.deletePendingEmailService.onCancelPendingEmail().subscribe(
      () => {
        // Optionally refresh the settings or reset the form
        this.ngOnInit();
      },
      (error: any) => {
        this.messages.push({
          severity: "error",
          detail: this.translateService.getText("Acc_Error_CancelPendingEmail", "There was a problem cancelling the pending email address."),
          summary: this.translateService.getText("ErrorSummary", "Error!")
        });
      }
    );
  this.pendingEmail = null; // Clear pendingEmail on cancel
  this.accountForm.get('email').setValue(this.setting.contact.email);
  }

  ///////////////////////////////////////////////////////////////////////////
  // Helper methods
  ///////////////////////////////////////////////////////////////////////////

  // Reloads the phone form array state when the underlying view model has changed.
  private setPhones(phones: PhoneInfo[]): void {
    const phoneFieldGroups = phones.map(phone => {
      return this.formBuilder.group({
        id: phone.id,
        isTextEnabled: phone.isTextEnabled,
        number: [
          phone.number,
          [Validators.required, Validators.pattern(this.PHONE_VALIDATION)]
        ],
        phoneType: phone.phoneType,
        verificationStatus: phone.verificationStatus
      });
    });
    const phoneFormArray = this.formBuilder.array(phoneFieldGroups);
    this.accountForm.setControl("phones", phoneFormArray);
  }

  // Creates the form for this component when initially entered. Note that
  // when entered the initial view model is not loaded; therefore, the control
  // values are emptry and the validations are minimal.
  private createForm(): void {
    this.accountForm = this.formBuilder.group({
      // Modified by AI on 05/06/2026 — PBI #3034916: added noSpecialCharsValidator to firstName and lastName
      firstName: ["", [Validators.required, this.noSpecialCharsValidator()]],
      lastName: ["", [Validators.required, this.noSpecialCharsValidator()]],
      email: [
        "",
        [Validators.required, Validators.pattern(this.EMAIL_VALIDATION)]
      ],
      phones: this.formBuilder.array([this.createPhone()]),
      cultureInfo: [""]
    });

    // The following values are not strictly necessary, but are provided
    // for html template binding convenience.
    this.firstName = this.accountForm.controls["firstName"];
    this.lastName = this.accountForm.controls["lastName"];
    this.email = this.accountForm.controls["email"];

    // Clear duplicate email error when email field changes
    this.email.valueChanges.subscribe(() => {
      this.duplicateEmailError = "";
    });
  }

  // Creates a new form group for the phone array.
  private createPhone(phone: PhoneInfo = null): FormGroup {
    if (phone == null) {
      phone = this.createEmptyPhone();
    }
    return this.formBuilder.group({
      id: phone.id,
      isTextEnabled: phone.isTextEnabled,
      number: [
        phone.number,
        [Validators.required, Validators.pattern(this.PHONE_VALIDATION)]
      ],
      phoneType: phone.phoneType,
      verificationStatus: phone.verificationStatus
    });
  }

  // A convenience method to initialize a form group based on a backing
  // PhoneInfo model.
  private createEmptyPhone(): PhoneInfo {
    const pi: PhoneInfo = {
      id: this.getPhoneId(),
      isTextEnabled: false,
      number: "",
      phoneType: this.DEFAULT_PHONETYPE,
      verificationStatus: this.DEFAULT_STATUS
    };
    return pi;
  }

  // Generated by AI on 05/06/2026 — PBI #3034916: blocks special characters in name fields
  // Exclusion list: < > / = ? # @ " ; \ { } ` &
  private noSpecialCharsValidator(): ValidatorFn {
    const NAME_SPECIAL_CHARS = /[<>/=?#@";\\\{\}`&]/;
    return (control: AbstractControl): {[key: string]: any} => {
      const value: string = control.value;
      if (!value) { return null; }
      return NAME_SPECIAL_CHARS.test(value) ? { noSpecialChars: true } : null;
    };
  }

  // Saves the component's view model using the user account service.
  private save(shouldReload: boolean, originalEmail?: string, newEmail?: string): void {
    const MIN_TIMEOUT_PERIOD: number = 0.083;
    const timeoutPeriod: number = this.setting.security.sessionTimeout;

    this.setting.security.sessionTimeout =
      timeoutPeriod < MIN_TIMEOUT_PERIOD ? MIN_TIMEOUT_PERIOD : timeoutPeriod;
    this.busy = this.accountService.saveSettings(this.setting).subscribe(
      setting => {
        this.setting.contact = setting.contact;
        this.setting.security.cultureInfo = this.setting.security.cultureInfo;
        this.duplicateEmailError = ""; // Clear error on success
        // After successful save, revert the form value to the original email and set pendingEmail
        if (originalEmail && newEmail && newEmail !== originalEmail) {
          this.accountForm.get('email').setValue(originalEmail);
          this.pendingEmail = newEmail;
        } else {
          this.pendingEmail = null;
        }
        this.messages.push({
          severity: "info",
          detail: this.translateService.getText("Acc_Success_Message", "Your account settings were saved."),
          summary: this.translateService.getText("Common.Success","Success")
        });
        this.setTranslationLanguage();
        if (shouldReload) {
          location.reload();
        }
      },
      error => {
        console.log(error);
        if (error.detail === "This email is already in use. Each account must have a unique email address.") {
          this.duplicateEmailError = error.detail;
        } else {
          this.duplicateEmailError = "";
          if (error.source) {
            for (var err in error.source) {
              this.messages.push({
                severity: "error",
                detail: error.source[err],
                summary: error.title == "" ? this.translateService.getText("Error", "An error occurred.") : error.title
              });
            }
          } else {
            this.messages.push({
              severity: "error",
              detail:
                error.detail == ""
                  ? this.translateService.getText("Acc_Error_Save", "There was a problem saving your account settings.")
                  : error.detail,
              summary: error.title == "" ? this.translateService.getText("Error", "An error occurred.") : error.title
            });
          }
        }
      }
    );
  }

  private setTranslationLanguage() {
    if (this.setting.security.isLocalizationEnabled) {
      this.translateService.setLanguage(this.setting.security.cultureInfo);
    }
    else {
      this.translateService.setDefaultLang()
    }
  }
  // Generates a phoneId of a phoneInfo item.
  private getPhoneId(): number {
    if (this.phoneCount === 0) {
      return -1;
    } else {
      let negativeIds = this.setting.contact.phones
        .filter(t => t.id < 0)
        .map(t => t.id);
      if (negativeIds.length === 0) {
        return -1;
      } else {
        return Math.min.apply(Math, negativeIds) - 1;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/my-settings/account/account.component.ts